<script setup lang="ts">
import PrimaryButton from '@/components/utils/PrimaryButton/PrimaryButton.vue';
import { defineAsyncComponent, ref, shallowRef, type Component } from 'vue';
import { useIntervalFn } from '@vueuse/core'
import tryArrayIndex from '@/lib/tryArrayIndex';
import useAosRefresh from '@/composables/useAosRefresh';

interface Trade {
  profit: string
  date: string
  graph?: Component
}


import {useI18n} from "vue-i18n";
const {t} = useI18n({
  useScope: 'global'
});

const trades = [
  {
    profit: '+$3878.89',
    date: 'March 11 2024',
    graph: () => import('./snapshots/Graph3878.vue')
  },
  {
    profit: '+$3518.66',
    date: 'February 20 2024',
    graph: () => import('./snapshots/Graph3518.vue')
  },
  {
    profit: '+$2852.86',
    date: 'March 11 2024',
    graph: () => import('./snapshots/Graph2852.vue')
  },
  {
    profit: '+$3374.89',
    date: 'February 29 2024',
    graph: () => import('./snapshots/Graph3374.vue')
  },
] as const satisfies Trade[]

const currentIndex = ref<number>(0)
const currentTrade = shallowRef<(typeof trades)[number]>(tryArrayIndex(trades, 0))

useIntervalFn(() => {

  // rotate to next trade
  if (currentIndex.value >= trades.length - 1) {
    currentIndex.value = 0
  } else {
    currentIndex.value += 1
  }

  // change to next trade
  currentTrade.value = tryArrayIndex(trades, currentIndex.value)

}, 2500)

useAosRefresh()
</script>

<template>
  <section
    data-aos
    :class="[
      'w-full isolate pb-10',
      'duration-700 ease-in-out [&:not(.aos-animate)]:(opacity-0 translate-y-10)'
    ]">
    <div class="relative flex flex-col items-center relative mx-auto">
      <div class="text-brand-secondary inline text-sm font-medium text-center leading-[150%] z-1000">

        <span class="text-nowrap">
          DipSway {{ t('made') }}
          <span class="relative">
            <span class="invisible">{{ currentTrade.profit }}</span>
            <Transition enter-active-class="duration-400 ease-in-out" leave-active-class="duration-400 ease-in-out"
              enter-from-class="opacity-0 translate-y-1 scale-96" leave-to-class="opacity-0 translate-y-1 scale-96"
              mode="out-in">
              <span :key="currentIndex" class="absolute text-brand-gradient left-1/2 -translate-x-1/2  font-semibold">{{
                currentTrade.profit }}</span>
            </Transition>
          </span>
          {{ t('profit_on') }}
        </span>

        <br />

        <span>
          {{ t('a_single_trade') }} @
          <span>{{ currentTrade.date }}</span>
        </span>

      </div>
      <!-- Component -->
      <div class="min-w-160 inline-block min-h-100 flex items-center justify-center -z-1 relative">
        <Transition 
          enter-active-class="duration-500 ease-in-out" 
          leave-active-class="duration-300 ease-in-out"
          enter-from-class="opacity-0 translate-y-1 scale-96" 
          leave-to-class="opacity-0 translate-y-1 scale-95"
          mode="out-in"
        >
          <component :is="defineAsyncComponent(currentTrade.graph)" class="w-full h-full absolute fade-x-18" />
        </Transition>
      </div>
      <div class="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center container">
        <!-- Lighting start -->
        <div :class="[
          'bg-effect-lighting bg-brand-gradient opacity-40',
          'absolute top-1/2 left-2/8 -translate-y-1/2',
          'aspect-video w-40 h-80 rotate-35',
        ]" />
        <div :class="[
          'bg-effect-lighting bg-brand-gradient opacity-30',
          'absolute top-full left-3/8 -translate-y-1/2',
          'aspect-video w-40 h-140 rotate-35',
        ]" />
        <div :class="[
          'bg-effect-lighting bg-brand-gradient opacity-40',
          'absolute top-full left-5/8 -translate-y-1/2',
          'aspect-video w-40 h-80 rotate-35',
        ]" />
        <!-- Lighting end -->
        <h2 class="mt-27.5 text-center max-w-200 font-semibold heading-2 relative">
          {{ t('dont_go_back_to_manual_trading') }},
          <span class="text-brand-gradient"> {{t('get_in_before_the_pump')}}</span>
          →
          <span class="text-brand-gradient">{{ t('get_out_before_the_drop') }}</span>.
          {{ t('on_autopilot') }}.
        </h2>
        <PrimaryButton
            class="mt-9"
            href="/crypto-trading-bot-earnings-calculator">
          {{ t('start_your_dipsway_bot') }}
        </PrimaryButton>
      </div>
    </div>
  </section>
</template>
